/**
 * Prompt Library Integration Hook
 *
 * Integrates the prompt library API with the Zustand store.
 * This hook handles the bridge between React Query and the store,
 * similar to how useS3ChatIntegration works.
 */

import React from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';
import { useUserPromptsQuery } from './use-gateway-queries';
import { usePromptLibraryStore } from '../stores/prompt-library-store';

const logger = createLogger('client:hook:use-prompt-library-integration');

/**
 * Main hook for prompt library integration
 * Loads user prompts from WorkOS backend and syncs with store
 */
export const usePromptLibraryIntegration = () => {
  const store = usePromptLibraryStore();

  // Load user prompts from WorkOS backend
  const {
    data: promptsData,
    isLoading: isLoadingPrompts,
    error: promptsError,
    refetch,
  } = useUserPromptsQuery();

  // Extract stable references to avoid infinite re-renders
  const setUserPrompts = React.useCallback(store.setUserPrompts, []);
  const setIsPromptsLoading = React.useCallback(store.setIsPromptsLoading, []);

  // Sync prompts data with store when data loads
  React.useEffect(() => {
    if (promptsData?.body?.prompts) {
      logger.info('📚 [PROMPT-LIBRARY] Loading user prompts:', promptsData.body.prompts.length);
      setUserPrompts(promptsData.body.prompts);
      setIsPromptsLoading(false);
    }
  }, [promptsData, setUserPrompts, setIsPromptsLoading]);

  // Handle loading state
  React.useEffect(() => {
    setIsPromptsLoading(isLoadingPrompts);
  }, [isLoadingPrompts, setIsPromptsLoading]);

  // Handle errors
  React.useEffect(() => {
    if (promptsError) {
      logger.error('❌ [PROMPT-LIBRARY] Failed to load user prompts:', promptsError);
      setIsPromptsLoading(false);
    }
  }, [promptsError, setIsPromptsLoading]);

  return {
    isLoading: isLoadingPrompts,
    error: promptsError,
    refetch,
  };
};
